import type { LayoutItem, OriginalTrophy } from '@/api/trophies'

/** trophyId → position（棚に飾っているものだけ） */
export type ShelfLayout = Record<number, number>

export type DragSource = { from: 'shelf' | 'list'; trophyId: number }
export type DropTarget = { kind: 'slot'; pos: number } | { kind: 'list' }
export type DropResult = { next: ShelfLayout; items: LayoutItem[] }

export const OWNED_LIST_DROP_ID = 'owned-list'

export function layoutFromTrophies(trophies: OriginalTrophy[]): ShelfLayout {
  const layout: ShelfLayout = {}
  for (const t of trophies) if (t.position !== null) layout[t.id] = t.position
  return layout
}

/** 空いている最初のスロット（0 から数えて最小の未使用 position） */
export function firstEmptySlot(layout: ShelfLayout): number {
  const used = new Set(Object.values(layout))
  let pos = 0
  while (used.has(pos)) pos += 1
  return pos
}

// 同じトロフィーが棚と一覧の両方でドラッグ可能になるため、出どころを id に含める
export const toDragId = (s: DragSource): string => `${s.from}-${s.trophyId}`

export function parseDragId(id: string): DragSource | null {
  const m = /^(shelf|list)-(\d+)$/.exec(id)
  if (!m) return null
  return { from: m[1] as DragSource['from'], trophyId: Number(m[2]) }
}

export const toSlotId = (pos: number): string => `slot-${pos}`

export function parseDropId(id: string): DropTarget | null {
  if (id === OWNED_LIST_DROP_ID) return { kind: 'list' }
  const m = /^slot-(\d+)$/.exec(id)
  if (!m) return null
  return { kind: 'slot', pos: Number(m[1]) }
}

/**
 * ドロップ結果の配置を計算する。何も変わらない場合は null。
 * - スロットへ：空きなら置く／埋まっていれば入れ替え（未配置からなら元の占有者は棚から外れる）
 * - 一覧へ：棚から持ってきたものだけ外す
 */
export function resolveDrop(
  layout: ShelfLayout,
  knownIds: ReadonlySet<number>,
  source: DragSource,
  target: DropTarget,
): DropResult | null {
  const id = source.trophyId
  if (!knownIds.has(id)) return null
  const sourcePos: number | null = layout[id] ?? null

  if (target.kind === 'list') {
    if (source.from !== 'shelf' || sourcePos === null) return null
    const next = { ...layout }
    delete next[id]
    return { next, items: [{ id, position: null }] }
  }

  const targetPos = target.pos
  if (sourcePos === targetPos) return null

  const occupantEntry = Object.entries(layout).find(([, pos]) => pos === targetPos)
  const occupant = occupantEntry ? Number(occupantEntry[0]) : null

  const next: ShelfLayout = { ...layout, [id]: targetPos }
  const items: LayoutItem[] = [{ id, position: targetPos }]
  if (occupant !== null) {
    if (sourcePos === null) delete next[occupant]
    else next[occupant] = sourcePos
    items.push({ id: occupant, position: sourcePos })
  }
  return { next, items }
}
